import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    // Listener for auth state changes - set up BEFORE getSession
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;
        setSession(session);
        setUser(session?.user ?? null);
      }
    );

    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (session) {
          if (isMounted) {
            setSession(session);
            setUser(session.user);
          }
        } else {
          // No Supabase session - check for stored access token
          const storedToken = localStorage.getItem('solveiagro_access_token');
          if (storedToken) {
            try {
              const deviceId = localStorage.getItem('solveiagro_device_id') || crypto.randomUUID();
              localStorage.setItem('solveiagro_device_id', deviceId);

              const { data, error } = await supabase.functions.invoke('authenticate-token', {
                body: { token: storedToken },
                headers: { 'x-device-id': deviceId }
              });

              if (!error && data?.success && data.access_token && data.refresh_token) {
                const { data: sessionData } = await supabase.auth.setSession({
                  access_token: data.access_token,
                  refresh_token: data.refresh_token
                });
                if (isMounted && sessionData.session) {
                  setSession(sessionData.session);
                  setUser(sessionData.session.user);
                }
              } else {
                // Token invalid or expired - clean up
                localStorage.removeItem('solveiagro_access_token');
                localStorage.removeItem('token_session');
              }
            } catch {
              localStorage.removeItem('solveiagro_access_token');
              localStorage.removeItem('token_session');
            }
          }
        }
      } catch (error) {
        console.error('[useAuth] Error during initialization:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    localStorage.removeItem('solveiagro_access_token');
    localStorage.removeItem('token_session');
    
    if (session) {
      await supabase.auth.signOut();
    }
    
    window.location.href = '/auth';
  };

  // isAuthenticated now depends on a REAL session
  const isAuthenticated = !!session;

  return {
    user,
    session,
    loading,
    signOut,
    isAuthenticated
  };
}
